import React, {
  type ComponentType,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
} from "react";
import { Platform, StyleSheet } from "react-native";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { TitleBodyContent, palette, useSafeAreaInsets } from "@stepapp/ui";

import reducer from "./reducer";
import type { InAppNotificationRef, NotificationProps, TitleBodyProps } from "./types";

export const inAppNotificationRef = React.createRef<InAppNotificationRef>();

const InAppNotification = forwardRef<InAppNotificationRef>((_, ref) => {
  const [{ type, props }, dispatch] = useReducer(reducer, {
    type: "titleBody",
    props: { avatarUrl: "", body: "", title: "", onPress: () => {} },
  });
  const insets = useSafeAreaInsets();
  const timeoutID = useRef<NodeJS.Timeout | null>(null);
  const transition = useSharedValue(0);

  const changeTransitionTo = useCallback(
    (toValue: number) => {
      transition.value = withTiming(toValue, {
        easing: Easing.out(Easing.cubic),
        duration: 300,
      });
    },
    [transition],
  );

  const closeNotification = useCallback(() => {
    if (timeoutID.current) {
      clearTimeout(timeoutID.current);
    }
    changeTransitionTo(0);
    setTimeout(() => dispatch({ type: "clearState" }), 250);
    props?.onPress?.();
  }, [changeTransitionTo, props]);

  const toggleNotification = useCallback(() => {
    changeTransitionTo(1);
    // @ts-ignore: NodeJS.Timeout is not defined in the global scope
    timeoutID.current = setTimeout(closeNotification, 3000);
  }, [changeTransitionTo, closeNotification]);

  const showTitleBodyNotification = useCallback(
    (props: TitleBodyProps) => {
      toggleNotification();
      dispatch({
        type: "setInAppNotificationParams",
        payload: { type: "titleBody", props },
      });
    },
    [toggleNotification],
  );

  useImperativeHandle(ref, () => ({ showTitleBodyNotification }), [showTitleBodyNotification]);

  useEffect(() => {
    return () => {
      if (timeoutID.current) {
        clearTimeout(timeoutID.current);
      }
    };
  }, []);

  const getContent = useCallback(() => {
    switch (type) {
      case "titleBody":
        return TitleBodyContent;

      default:
        return TitleBodyContent;
    }
  }, [type]);

  const Content: ComponentType<NotificationProps> = getContent();

  const animatedStyles = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: interpolate(transition.value, [0, 1], [-100, 20 + insets.top]),
      },
    ],
  }));

  return (
    <Animated.View onTouchEnd={closeNotification} style={[styles.container, animatedStyles]}>
      <Content {...props} />
    </Animated.View>
  );
});

export default InAppNotification;

const styles = StyleSheet.create({
  container: {
    backgroundColor: palette.common.white,
    elevation: 3,
    flex: 1,
    left: 0,
    position: "absolute",
    right: 0,
    top: -20,
    zIndex: 10,
    ...Platform.select({
      default: {
        shadowColor: palette.common.black,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.1,
        shadowRadius: 6,
      },
      web: {
        boxShadow: `0px 6px 6px ${palette.common.black}1A`,
      },
    }),
  },
});
